/**
 * DraftBatchPanel — "Genereer lichting": bulk AI-drafts klaar voor human approval.
 * - Handmatig: kies aantal → POST /draft-batch/run → polling tot klaar → toast.
 * - Dagelijks: toggle + aantal per dag (PATCH /draft-batch/settings).
 * Drafts landen in de Unified Inbox → tab "Upcoming" (pending approval); de
 * inbox ververst zichzelf elke 20s.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { toast } from "sonner";
import { Layers, Loader2, ChevronDown, ChevronUp, CalendarClock } from "lucide-react";

export default function DraftBatchPanel({ pillar = "sales" }) {
    const [settings, setSettings] = useState(null);
    const [count, setCount] = useState(pillar === "sales" ? 10 : 5);
    const [running, setRunning] = useState(false);
    const [open, setOpen] = useState(false);
    const [savingDaily, setSavingDaily] = useState(false);
    const pollRef = useRef(null);
    const countKey = pillar === "sales" ? "sales_count" : "recruit_count";

    useEffect(() => {
        api.get("/draft-batch/settings").then((r) => {
            setSettings(r.data);
            setCount(r.data[countKey] || (pillar === "sales" ? 10 : 5));
        }).catch(() => {});
        // Bij mount: loopt er nog een lichting?
        api.get("/draft-batch/latest", { params: { pillar } }).then((r) => {
            if (r.data?.status === "running") { setRunning(true); startPolling(); }
        }).catch(() => {});
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pillar]);

    const startPolling = () => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const r = await api.get("/draft-batch/latest", { params: { pillar } });
                const b = r.data || {};
                if (b.status === "done") {
                    clearInterval(pollRef.current);
                    setRunning(false);
                    toast.success(i18n.t("batch.done", { created: b.created, skipped: b.skipped || 0 }));
                } else if (b.status === "failed") {
                    clearInterval(pollRef.current);
                    setRunning(false);
                    toast.error(b.detail || i18n.t("batch.failed"));
                }
            } catch { /* stil */ }
        }, 3000);
    };

    const run = async () => {
        setRunning(true);
        try {
            await api.post("/draft-batch/run", { pillar, limit: count });
            toast.info(i18n.t("batch.started", { count }));
            startPolling();
        } catch (e) {
            setRunning(false);
            toast.error(e?.response?.data?.detail || i18n.t("batch.failed"));
        }
    };

    const saveDaily = async (patch) => {
        setSavingDaily(true);
        try {
            const r = await api.patch("/draft-batch/settings", patch);
            setSettings(r.data);
            toast.success(i18n.t("batch.daily_saved"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("batch.failed")); }
        finally { setSavingDaily(false); }
    };

    return (
        <div className="mb-4 rounded-xl border border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/[0.04] px-5 py-4" data-testid={`draft-batch-panel-${pillar}`}>
            <div className="flex items-center gap-3 flex-wrap">
                <Layers size={15} className="text-[var(--jr-brand)] shrink-0" />
                <div className="min-w-0">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-brand)]">{i18n.t("batch.title")}</div>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5">
                        {i18n.t(pillar === "sales" ? "batch.sub_sales" : "batch.sub_recruit")}
                    </p>
                </div>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                    <input type="number" min={1} max={25} value={count}
                        onChange={(e) => setCount(Math.max(1, Math.min(25, Number(e.target.value) || 1)))}
                        className="w-16 bg-transparent border border-white/15 rounded-lg px-2 py-1.5 text-xs text-white text-center"
                        data-testid={`batch-count-input-${pillar}`} disabled={running} />
                    <button onClick={run} disabled={running}
                        data-testid={`batch-run-btn-${pillar}`}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:opacity-85 disabled:opacity-50">
                        {running ? <Loader2 size={12} className="animate-spin" /> : <Layers size={12} />}
                        {running ? i18n.t("batch.running") : i18n.t("batch.run")}
                    </button>
                    <button onClick={() => setOpen(!open)} className="text-[var(--jr-text-3)] p-1" data-testid={`batch-daily-toggle-open-${pillar}`}>
                        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                </div>
            </div>
            {open && settings && (
                <div className="mt-3 pt-3 border-t border-white/8 flex items-center gap-4 flex-wrap" data-testid={`batch-daily-panel-${pillar}`}>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={!!settings.daily_enabled} disabled={savingDaily}
                            onChange={(e) => saveDaily({ daily_enabled: e.target.checked })}
                            className="accent-[var(--jr-brand)]" data-testid={`batch-daily-enabled-${pillar}`} />
                        <span className="text-xs text-white inline-flex items-center gap-1.5">
                            <CalendarClock size={12} className="text-[var(--jr-brand)]" /> {i18n.t("batch.daily_label")}
                        </span>
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-[var(--jr-text-3)]">
                        {i18n.t("batch.daily_sales")}
                        <input type="number" min={0} max={25} defaultValue={settings.sales_count} disabled={savingDaily}
                            onBlur={(e) => saveDaily({ sales_count: Math.max(0, Math.min(25, Number(e.target.value) || 0)) })}
                            className="w-14 bg-transparent border border-white/15 rounded px-1.5 py-1 text-xs text-white text-center"
                            data-testid={`batch-daily-sales-count-${pillar}`} />
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-[var(--jr-text-3)]">
                        {i18n.t("batch.daily_recruit")}
                        <input type="number" min={0} max={25} defaultValue={settings.recruit_count} disabled={savingDaily}
                            onBlur={(e) => saveDaily({ recruit_count: Math.max(0, Math.min(25, Number(e.target.value) || 0)) })}
                            className="w-14 bg-transparent border border-white/15 rounded px-1.5 py-1 text-xs text-white text-center"
                            data-testid={`batch-daily-recruit-count-${pillar}`} />
                    </label>
                    {savingDaily && <Loader2 size={12} className="animate-spin text-[var(--jr-text-3)]" />}
                    <span className="text-[10px] text-[var(--jr-text-3)] italic">{i18n.t("batch.daily_note")}</span>
                </div>
            )}
        </div>
    );
}
